import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import "./ExperiencePage.css";

/**
 * THE EVERYTHING ECO EXPERIENCE — a scroll-driven cinematic brand film.
 * Scrolling scrubs a slow camera move across full-screen scenes (Ken Burns),
 * with a "dwell engine" that slows the motion where text appears, plus
 * film grain, ambient particles, vignette, scene tints, letter-split titles,
 * glass quote cards and a custom LERP cursor. Zero video files needed —
 * an AI film clip can replace the stills later without changing the engine.
 */

const PUBLIC = process.env.PUBLIC_URL || "";

type Cam = { s: number; x: number; y: number };
type Scene = {
  src: string;
  kicker: string;
  title: string;
  body?: string;
  quote?: boolean;
  from: Cam;
  to: Cam;
  tint: string;
  align: "left" | "right";
  /** Scroll-scrubbed frame sequence (the "city heals as you scroll" scene). */
  frames?: { base: string; count: number; ext: string };
};

const SCENES: Scene[] = [
  {
    src: `${PUBLIC}/nyc-hero.png`,
    kicker: "EVERYTHING ECO PRESENTS",
    title: "TRASH TO TECH",
    body: "Born in Williamsburg. Built for all of New York City.",
    from: { s: 1.32, x: -0.7, y: -0.3 }, to: { s: 1.12, x: 0.5, y: 0.2 },
    tint: "#1f7ae0", align: "left",
  },
  {
    src: `${PUBLIC}/community-hero.png`,
    kicker: "WASTE TO WORTH",
    title: "PEOPLE ARE OUR PURPOSE",
    body: "“Nothing with value should ever be wasted. Not electronics. Not neighborhoods. Not opportunities. And certainly not people.”",
    quote: true,
    from: { s: 1.14, x: 0.6, y: 0.2 }, to: { s: 1.34, x: -0.4, y: -0.2 },
    tint: "#15a35a", align: "right",
  },
  {
    src: `${PUBLIC}/battle-bg.png`,
    kicker: "PAIN TO PURPOSE",
    title: "THE ECO-FORCE RISES",
    body: "Comics. Games. Toys. A universe where cleaning up your block makes you the hero.",
    from: { s: 1.3, x: -0.5, y: 0.3 }, to: { s: 1.1, x: 0.6, y: -0.3 },
    tint: "#7a3bd6", align: "left",
  },
  {
    src: `${PUBLIC}/exp-heal/f001.webp`,
    frames: { base: `${PUBLIC}/exp-heal/f`, count: 60, ext: "webp" },
    kicker: "WATCH WHAT SECOND CHANCES DO",
    title: "THE BLOCK COMES BACK TO LIFE",
    body: "You control this. Scroll slowly — trash disappears, trees grow, the sky clears. That's the job, one street at a time.",
    from: { s: 1, x: 0, y: 0 }, to: { s: 1.06, x: 0, y: 0 },
    tint: "#22b573", align: "left",
  },
  {
    src: `${PUBLIC}/scene-williamsburg.png`,
    kicker: "THE FUTURE WE'RE BUILDING",
    title: "WILLIAMSBURG, REIMAGINED",
    body: "Solar roofs. Community gardens. An Everything Eco hub on the corner. This is where we're going — block by block.",
    from: { s: 1.35, x: -0.6, y: 0.2 }, to: { s: 1.1, x: 0.55, y: -0.25 },
    tint: "#15a35a", align: "right",
  },
  {
    src: `${PUBLIC}/toy-earthjr.png`,
    kicker: "MEET THE MASCOT",
    title: "EARTH JR. & FRIENDS",
    body: "Collectible heroes — and free mini-figures for kids on real pickups.",
    from: { s: 1.28, x: 0.4, y: -0.3 }, to: { s: 1.08, x: -0.4, y: 0.3 },
    tint: "#ef7415", align: "right",
  },
];

const SCENE_VH = 1.7; // scroll length per scene, in viewport heights
const FINALE_VH = 1.3;

/** Monotonic ease that slows in the middle of each scene (where the text lives). */
function dwell(t: number, reduced: boolean): number {
  if (reduced) return t;
  const k = 0.55;
  return t + (k * Math.sin(t * Math.PI * 2)) / (Math.PI * 2);
}

function makeGrain(): HTMLCanvasElement {
  const cv = document.createElement("canvas");
  cv.width = 96; cv.height = 96;
  const c = cv.getContext("2d")!;
  const d = c.createImageData(96, 96);
  for (let i = 0; i < d.data.length; i += 4) {
    const v = (Math.random() * 255) | 0;
    d.data[i] = v; d.data[i + 1] = v; d.data[i + 2] = v; d.data[i + 3] = 26;
  }
  c.putImageData(d, 0, 0);
  return cv;
}

const ExperiencePage: React.FC = () => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const sectionRefs = useRef<(HTMLDivElement | null)[]>([]);
  const dotRefs = useRef<(HTMLDivElement | null)[]>([]);
  const cursorDot = useRef<HTMLDivElement>(null);
  const cursorRing = useRef<HTMLDivElement>(null);
  const [ready, setReady] = useState(false);

  useEffect(() => {
    let alive = true;
    const imgs: HTMLImageElement[] = [];
    Promise.all(
      SCENES.map(
        (sc) =>
          new Promise<HTMLImageElement>((res) => {
            const im = new Image();
            im.onload = () => res(im);
            im.onerror = () => res(im);
            im.src = sc.src;
          })
      )
    ).then((loaded) => {
      if (!alive) return;
      imgs.push(...loaded);
      setReady(true);
    });

    // Background-load the scroll-scrub frame sequence (city transformation).
    // Frames appear progressively; until one is loaded we fall back to the scene still.
    const frameImgs: HTMLImageElement[] = [];
    for (const sc of SCENES) {
      if (!sc.frames) continue;
      for (let k = 1; k <= sc.frames.count; k++) {
        const im = new Image();
        im.src = `${sc.frames.base}${String(k).padStart(3, "0")}.${sc.frames.ext}`;
        frameImgs[k - 1] = im;
      }
    }

    const reduced = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    const fine = window.matchMedia("(pointer: fine)").matches;
    const grain = makeGrain();

    // ambient particles
    const parts = Array.from({ length: reduced ? 0 : 40 }, () => ({
      x: Math.random(), y: Math.random(),
      r: 0.3 + Math.random() * 1.5,
      v: 0.008 + Math.random() * 0.02,
      o: 0.05 + Math.random() * 0.3,
      w: Math.random() * 7,
    }));

    // custom cursor state
    const cur = { x: -100, y: -100, rx: -100, ry: -100 };
    const onMove = (e: MouseEvent) => { cur.x = e.clientX; cur.y = e.clientY; };
    if (fine) window.addEventListener("mousemove", onMove, { passive: true });

    let raf = 0;
    let frame = 0;
    const canvas = canvasRef.current;
    const ctx = canvas ? canvas.getContext("2d") : null;

    const tick = () => {
      raf = requestAnimationFrame(tick);
      if (!canvas || !ctx) return;
      frame++;

      const W = window.innerWidth, H = window.innerHeight;
      const dpr = Math.min(window.devicePixelRatio || 1, 2);
      if (canvas.width !== W * dpr || canvas.height !== H * dpr) {
        canvas.width = W * dpr; canvas.height = H * dpr;
        canvas.style.width = W + "px"; canvas.style.height = H + "px";
      }
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);

      const L = H * SCENE_VH;
      const total = SCENES.length * L;
      const y = window.scrollY;
      const idxF = Math.min(SCENES.length - 1 + 0.9999, y / L);
      const idx = Math.min(SCENES.length - 1, idxF | 0);
      const t = Math.min(1, Math.max(0, (y - idx * L) / L));
      const finaleT = Math.min(1, Math.max(0, (y - total) / (H * 0.7)));

      const drawScene = (i: number, tt: number, alpha: number) => {
        let im = imgs[i];
        const sc = SCENES[i];
        const e = dwell(tt, reduced);
        // frame-scrub scene: pick the frame matching scroll progress (nearest loaded)
        if (sc.frames) {
          const want = Math.round(e * (sc.frames.count - 1));
          for (let k = want; k >= 0; k--) {
            const f = frameImgs[k];
            if (f && f.complete && f.naturalWidth) { im = f; break; }
          }
        }
        if (!im || !im.width) return;
        const s = sc.from.s + (sc.to.s - sc.from.s) * e;
        const px = sc.from.x + (sc.to.x - sc.from.x) * e;
        const py = sc.from.y + (sc.to.y - sc.from.y) * e;
        const ir = im.width / im.height, cr = W / H;
        let dw: number, dh: number;
        if (ir > cr) { dh = H; dw = H * ir; } else { dw = W; dh = W / ir; }
        dw *= s; dh *= s;
        const mx = (dw - W) / 2, my = (dh - H) / 2;
        ctx.globalAlpha = alpha;
        ctx.drawImage(im, (W - dw) / 2 + px * mx, (H - dh) / 2 + py * my, dw, dh);
        ctx.globalAlpha = 1;
      };

      ctx.fillStyle = "#07080f";
      ctx.fillRect(0, 0, W, H);
      drawScene(idx, t, 1);
      // crossfade from previous scene at the seam
      if (t < 0.1 && idx > 0) {
        ctx.globalAlpha = 1 - t / 0.1;
        drawScene(idx - 1, 1, 1 - t / 0.1);
        ctx.globalAlpha = 1;
      }

      // scene tint
      const tint = ctx.createLinearGradient(0, 0, W, H);
      tint.addColorStop(0, SCENES[idx].tint + "18");
      tint.addColorStop(1, "#ff8a4a10");
      ctx.fillStyle = tint;
      ctx.fillRect(0, 0, W, H);

      // vignette
      const vg = ctx.createRadialGradient(W / 2, H / 2, Math.min(W, H) * 0.42, W / 2, H / 2, Math.max(W, H) * 0.75);
      vg.addColorStop(0, "rgba(14,13,12,0)");
      vg.addColorStop(1, "rgba(14,13,12,.5)");
      ctx.fillStyle = vg;
      ctx.fillRect(0, 0, W, H);

      // particles
      for (const p of parts) {
        p.y -= p.v / 100;
        if (p.y < -0.02) { p.y = 1.02; p.x = Math.random(); }
        const sway = Math.sin(frame / 60 + p.w) * 8;
        ctx.globalAlpha = p.o;
        ctx.fillStyle = "#ffe9c8";
        ctx.beginPath();
        ctx.arc(p.x * W + sway, p.y * H, p.r, 0, 7);
        ctx.fill();
      }
      ctx.globalAlpha = 1;

      // film grain
      if (!reduced && frame % 2 === 0) {
        ctx.globalAlpha = 0.055;
        const ox = (Math.random() * 96) | 0, oy = (Math.random() * 96) | 0;
        for (let gx = -ox; gx < W; gx += 96) for (let gy = -oy; gy < H; gy += 96) ctx.drawImage(grain, gx, gy);
        ctx.globalAlpha = 1;
      }

      // darken under finale
      if (finaleT > 0) {
        ctx.fillStyle = `rgba(6,8,14,${0.82 * finaleT})`;
        ctx.fillRect(0, 0, W, H);
      }

      // ---- text overlays ----
      for (let i = 0; i < SCENES.length; i++) {
        const el = sectionRefs.current[i];
        if (!el) continue;
        let vis = 0;
        if (i === idx) {
          vis = Math.min(1, Math.max(0, (t - 0.22) / 0.12)) * Math.min(1, Math.max(0, (0.85 - t) / 0.12));
        }
        el.style.opacity = String(vis);
        el.style.transform = `translateY(${(1 - vis) * 28}px)`;
        el.style.pointerEvents = vis > 0.5 ? "auto" : "none";
        if (vis > 0.05) el.classList.add("on"); else el.classList.remove("on");
      }
      for (let i = 0; i < SCENES.length + 1; i++) {
        const d = dotRefs.current[i];
        if (!d) continue;
        const active = finaleT > 0.2 ? i === SCENES.length : i === idx;
        d.classList.toggle("active", active);
      }

      // cursor LERP
      if (fine && cursorDot.current && cursorRing.current) {
        cur.rx += (cur.x - cur.rx) * 0.16;
        cur.ry += (cur.y - cur.ry) * 0.16;
        cursorDot.current.style.transform = `translate(${cur.x - 3}px, ${cur.y - 3}px)`;
        cursorRing.current.style.transform = `translate(${cur.rx - 18}px, ${cur.ry - 18}px)`;
      }
    };
    raf = requestAnimationFrame(tick);

    return () => {
      alive = false;
      cancelAnimationFrame(raf);
      if (fine) window.removeEventListener("mousemove", onMove);
    };
  }, []);

  useEffect(() => {
    window.scrollTo(0, 0);
    document.body.classList.add("ee-film-body");
    return () => document.body.classList.remove("ee-film-body");
  }, []);

  const totalHeight = `calc(${SCENES.length * SCENE_VH * 100}vh + ${FINALE_VH * 100}vh)`;

  return (
    <div className="ee-film" style={{ height: totalHeight }}>
      <canvas ref={canvasRef} className="ee-film-canvas" />

      {!ready && (
        <div className="ee-film-loading">
          <div className="ee-film-loading-word">EVERYTHING ECO</div>
          <div className="ee-film-loading-sub">LOADING THE EXPERIENCE…</div>
        </div>
      )}

      {/* top chrome */}
      <Link to="/" className="ee-film-exit">✕ EXIT</Link>
      <div className="ee-film-brand">EVERYTHING&nbsp;ECO</div>
      <div className="ee-film-hint">SCROLL ▾</div>

      {/* side progress dots */}
      <div className="ee-film-dots">
        {[...SCENES.map((s) => s.kicker), "BE A HERO"].map((k, i) => (
          <div key={k} ref={(el) => { dotRefs.current[i] = el; }} className="ee-film-dot" title={k} />
        ))}
      </div>

      {/* scene text overlays */}
      {SCENES.map((sc, i) => (
        <div
          key={sc.title}
          ref={(el) => { sectionRefs.current[i] = el; }}
          className={`ee-film-sec ${sc.align}${sc.quote ? " quote" : ""}`}
          style={{ opacity: 0 }}
        >
          <div className="ee-film-kicker" style={{ color: sc.tint === "#15a35a" ? "#5ee8a4" : "#ffd9a0" }}>{sc.kicker}</div>
          <h2 className="ee-film-title">
            {(() => {
              let ci = 0;
              return sc.title.split(" ").map((word, wi) => (
                <span key={wi} className="ee-film-word">
                  {word.split("").map((ch) => {
                    const delay = ci++ * 28;
                    return (
                      <span key={`${wi}-${delay}`} className="ee-film-ch" style={{ transitionDelay: `${delay}ms` }}>
                        {ch}
                      </span>
                    );
                  })}
                </span>
              ));
            })()}
          </h2>
          {sc.body && <p className="ee-film-body">{sc.body}</p>}
          {sc.quote && <div className="ee-film-attr">— JULIO, FOUNDER · BROOKLYN BORN</div>}
        </div>
      ))}

      {/* finale */}
      <div className="ee-film-finale" style={{ top: `${SCENES.length * SCENE_VH * 100}vh` }}>
        <img src={`${PUBLIC}/logo-full.png`} alt="Everything Eco" className="ee-film-logo" />
        <h2 className="ee-film-finale-title">BE A HERO FOR REAL</h2>
        <div className="ee-film-stats">
          <div className="ee-film-stat"><strong>FREE</strong><span>PICKUPS & DONATIONS</span></div>
          <div className="ee-film-stat"><strong>BK</strong><span>BROOKLYN BORN</span></div>
          <div className="ee-film-stat"><strong>♻️</strong><span>E-WASTE RESCUED</span></div>
          <div className="ee-film-stat"><strong>🔒</strong><span>SECURE DATA DESTRUCTION</span></div>
        </div>
        <div className="ee-film-ctas">
          <Link to="/pickup" className="ee-film-btn green">📦 SCHEDULE A FREE PICKUP</Link>
          <Link to="/game" className="ee-film-btn blue">🎮 PLAY MISSION NYC</Link>
        </div>
        <Link to="/" className="ee-film-back">← Back to the site</Link>
      </div>

      {/* custom cursor */}
      <div ref={cursorDot} className="ee-film-cursor-dot" />
      <div ref={cursorRing} className="ee-film-cursor-ring" />
    </div>
  );
};

export default ExperiencePage;
